import { List } from '../constants-types/PiList.model';
import {
    DreamTeamFunctionType,
    DreamTeamCrossFunctionId,
    TeamImprovementId
} from '../constants-types/StrategyConstants';
import { UserFolderAssociation } from './Folder.model';
import { ResultBasics } from './ResultBasics.model';
import { User, UserStub } from './User.model';

export class Group implements ResultBasics {
    readonly id: string;
    readonly name: string;
    readonly teamFunctionType: DreamTeamFunctionType;
    readonly otherTeamFunction: string;
    readonly crossFunctionId: DreamTeamCrossFunctionId;
    readonly teamImprovementIds: TeamImprovementId[] = [];
    readonly otherTeamImprovement: string;
    readonly isStrategyAutoSelectionEnabled: boolean;
    readonly parentId: string = '';
    readonly peopleAndJobCount: number = 0;
    readonly isEditableByUser: boolean;
    readonly isEditableBySharedUsers: boolean;
    readonly groupOwnerId?: string;
    readonly modifiedTime: Date;
    readonly createdTime: Date;
    readonly groupOwner: UserStub;
    // These properties are not readonly since they will be explicitly set in Groups.dataservice after data is retrieved from the connect layer (see fetchGroups() method in Groups.dataservice)
    hasTeamDiscoverySession = false;
    isHidden: boolean;

    constructor(startingValues?: Partial<Group>) {
        Object.assign(this, startingValues);
    }
}

export class GroupItem {
    readonly personId: string;
    readonly isManager: boolean;
}

export class GroupUpdate {
    originalObject: Group | null;
    name?: string;
    teamFunctionType: DreamTeamFunctionType;
    otherTeamFunction: string;
    crossFunctionId: DreamTeamCrossFunctionId;
    teamImprovementIds: TeamImprovementId[];
    otherTeamImprovement: string;
    isStrategyAutoSelectionEnabled: boolean;
    isEditableBySharedUsers?: boolean;

    constructor(firstArgument: Group, secondArgument: Partial<GroupUpdate>) {
        if (firstArgument instanceof Group) {
            this.originalObject = firstArgument;
        }
        Object.assign(this, firstArgument, secondArgument);
    }
}

export class GroupItemUpdate {
    originalObject: GroupItem | null;
    personId: string;
    isManager: boolean;
}

export type SharedGroupWithUsers = {
    group: Group;
    directUsers?: List<User>;
    thirdPartyUsers?: List<User>;
};

export type SharedGroupValidationConflictItem = {
    id: string;
    folderId: string;
    name: string;
    type: 'person' | 'job';
};

export type SharedGroupValidationConflictFolder = {
    id: string;
    name: string;
    path: string;
    problemItems: SharedGroupValidationConflictItem[];
};

export type GroupValidateResponse = {
    groupId: string;
    userId: string;
    userName: string;
    problemFolders: SharedGroupValidationConflictFolder[];
};

export type SharedGroupUserAccess = {
    groupId: string;
    userFolderCombos: UserFolderAssociation[];
};

export type DreamTeamInsights = {
    title: string;
    version: string;
    teamSizes: {
        overallAverageSize: number;
        sizesByTeamFunction: { [key: string]: number };
    };
    teamStrategyFrequencies: {
        teamStrategyFrequencyPercentagesByTeamTypeAndStrategy: {
            [key: string]: { [key: string]: number };
        };
    };
    teamTypeFrequencies: {
        teamTypeFrequencyPercentagesByTeamType: { [key: string]: number };
        teamTypeFrequencyPercentagesByTeamFunctionAndType: {
            [key: string]: { [key: string]: number };
        };
    };
    teamActionRankings: Map<string, DreamTeamActionRanking>;
};

export type DreamTeamActionsDefinition = {
    teamActionsByTeamType: Map<string, DreamTeamActionText[]>;
};

export type DreamTeamActionText = {
    id: number;
    translationKey: string;
    defaultText: string;
    tags: string;
    averageRanking?: number;
    ratingCount?: number;
};

export type DreamTeamActionRanking = {
    actionId: number;
    averageRanking: number;
    ratingCount: number;
};
